export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const base64String = reader.result as string
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64Content = base64String.split(',')[1]
      resolve(base64Content)
    }
    reader.onerror = error => reject(error)
  })
}

interface CompressImageOptions {
  /** Longest side of the resulting image, in pixels. */
  maxDimension?: number
  /** JPEG quality, 0 to 1. */
  quality?: number
}

/**
 * Shrinks a photo before it is turned into base64 and posted.
 *
 * Phones shoot 8 to 12 megapixel frames; base64 inflates them by another third,
 * so an untouched shot easily becomes a 15 MB request body that a site
 * connection drops halfway through - which surfaces in the app as a bare
 * "Failed to fetch". A 1920px JPEG keeps every detail an installation photo
 * needs while staying a few hundred kilobytes.
 *
 * Anything unexpected (an unsupported codec, a browser without canvas encoding)
 * falls back to the original file: a large upload is better than none.
 */
export const compressImage = async (
  file: File,
  { maxDimension = 1920, quality = 0.8 }: CompressImageOptions = {}
): Promise<File> => {
  if (!file.type.startsWith('image/')) return file

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height))

    // Already small enough and already a JPEG: re-encoding would only lose quality
    if (scale === 1 && file.type === 'image/jpeg') {
      bitmap.close()
      return file
    }

    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)

    const context = canvas.getContext('2d')
    if (!context) {
      bitmap.close()
      return file
    }

    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    )

    if (!blob || blob.size >= file.size) return file

    const name = file.name.replace(/\.[^.]+$/, '') || 'photo'
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified })
  } catch (error) {
    console.warn('Image compression failed, uploading the original file:', error)
    return file
  }
}

export const getFileMetadata = (file: File) => {
  return {
    fileName: file.name,
    mimeType: file.type,
    uploadTimestamp: BigInt(Date.now())
  }
}
